import React, { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import {
	Activity,
	Clock,
	Search,
	Filter,
	CheckCircle,
	FileText,
	Users,
	Settings,
	AlertCircle,
	Upload,
	Download,
	Edit2,
	Plus,
	Trash2,
	RefreshCw,
	Calendar,
	ChevronDown,
} from 'lucide-react'
import { Button } from '@components/common'

/**
 * ActivityTab Component
 *
 * Displays comprehensive project activity history
 * with filtering, search, and grouping by date
 */
const ActivityTab = ({ activities, onRefresh }) => {
	const [searchQuery, setSearchQuery] = useState('')
	const [activeFilter, setActiveFilter] = useState('all')
	const [expandedDays, setExpandedDays] = useState({})

	// Activity type configurations
	const activityTypes = {
		status_change: {
			icon: RefreshCw,
			label: 'Status Change',
			color: '#3B82F6',
		},
		checklist_update: {
			icon: CheckCircle,
			label: 'Checklist Update',
			color: '#10B981',
		},
		document_upload: {
			icon: Upload,
			label: 'Document Upload',
			color: '#F59E0B',
		},
		document_delete: {
			icon: Trash2,
			label: 'Document Deleted',
			color: '#EF4444',
		},
		team_update: {
			icon: Users,
			label: 'Team Update',
			color: '#8B5CF6',
		},
		project_update: {
			icon: Edit2,
			label: 'Project Update',
			color: '#06B6D4',
		},
		comment: {
			icon: FileText,
			label: 'Comment',
			color: '#6B7280',
		},
		approval: {
			icon: CheckCircle,
			label: 'Approval',
			color: '#10B981',
		},
		default: {
			icon: Activity,
			label: 'Activity',
			color: '#6B7280',
		},
	}

	// Filter options
	const filterOptions = [
		{ value: 'all', label: 'All Activity' },
		{ value: 'status_change', label: 'Status Changes' },
		{ value: 'checklist_update', label: 'Checklist Updates' },
		{ value: 'document_upload', label: 'Documents' },
		{ value: 'team_update', label: 'Team Updates' },
	]

	// Format relative time
	const formatRelativeTime = (timestamp) => {
		if (!timestamp) return ''

		const now = new Date()
		const date = new Date(timestamp)
		const diffMs = now - date
		const diffMins = Math.floor(diffMs / 60000)
		const diffHours = Math.floor(diffMins / 60)
		const diffDays = Math.floor(diffHours / 24)

		if (diffMins < 1) return 'Just now'
		if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`
		if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
		if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`

		return date.toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
			year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
		})
	}

	// Format time only
	const formatTime = (timestamp) => {
		if (!timestamp) return ''
		return new Date(timestamp).toLocaleTimeString('en-US', {
			hour: 'numeric',
			minute: '2-digit',
			hour12: true,
		})
	}

	// Get day key for grouping
	const getDayKey = (timestamp) => {
		if (!timestamp) return 'Unknown'
		const date = new Date(timestamp)
		const today = new Date()
		const yesterday = new Date(today)
		yesterday.setDate(yesterday.getDate() - 1)

		if (date.toDateString() === today.toDateString()) {
			return 'Today'
		}
		if (date.toDateString() === yesterday.toDateString()) {
			return 'Yesterday'
		}

		return date.toLocaleDateString('en-US', {
			weekday: 'long',
			month: 'long',
			day: 'numeric',
			year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
		})
	}

	// Filter and search activities
	const filteredActivities = useMemo(() => {
		let filtered = activities

		// Apply type filter
		if (activeFilter !== 'all') {
			filtered = filtered.filter((activity) => activity.type === activeFilter)
		}

		// Apply search
		if (searchQuery.trim()) {
			const query = searchQuery.toLowerCase()
			filtered = filtered.filter(
				(activity) =>
					(activity.message || '').toLowerCase().includes(query) ||
					(activity.user || '').toLowerCase().includes(query)
			)
		}

		return filtered
	}, [activities, activeFilter, searchQuery])

	// Group activities by day
	const activitiesByDay = useMemo(() => {
		const groups = {}

		filteredActivities.forEach((activity) => {
			const dayKey = getDayKey(activity.timestamp)
			if (!groups[dayKey]) {
				groups[dayKey] = {
					date: dayKey,
					activities: [],
				}
			}
			groups[dayKey].activities.push(activity)
		})

		return Object.values(groups)
	}, [filteredActivities])

	// Toggle day expansion
	const toggleDay = (dayKey) => {
		setExpandedDays((prev) => ({
			...prev,
			[dayKey]: prev[dayKey] === false ? true : false,
		}))
	}

	// Get activity config
	const getActivityConfig = (type) => {
		return activityTypes[type] || activityTypes.default
	}

	return (
		<div className="activity-tab">
			{/* Toolbar */}
			<div className="activity-tab__toolbar">
				<div className="activity-tab__search">
					<Search size={16} />
					<input
						type="text"
						placeholder="Search activity..."
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
					/>
				</div>

				<div className="activity-tab__actions">
					<div className="activity-tab__filter-dropdown">
						<select
							value={activeFilter}
							onChange={(e) => setActiveFilter(e.target.value)}
							className="filter-select"
						>
							{filterOptions.map((option) => (
								<option key={option.value} value={option.value}>
									{option.label}
								</option>
							))}
						</select>
						<ChevronDown size={14} className="filter-select__icon" />
					</div>

					{onRefresh && (
						<Button variant="ghost" size="small" onClick={onRefresh}>
							<RefreshCw size={14} />
						</Button>
					)}
				</div>
			</div>

			{/* Summary */}
			<div className="activity-tab__summary">
				<span className="activity-tab__count">
					{filteredActivities.length}{' '}
					{filteredActivities.length === 1 ? 'activity' : 'activities'}
				</span>
				{activeFilter !== 'all' && (
					<Button
						variant="ghost"
						size="small"
						onClick={() => setActiveFilter('all')}
					>
						Clear filter
					</Button>
				)}
			</div>

			{/* Activity Groups */}
			{filteredActivities.length > 0 ? (
				<div className="activity-tab__timeline">
					{activitiesByDay.map((dayGroup) => {
						const isExpanded = expandedDays[dayGroup.date] !== false

						return (
							<div key={dayGroup.date} className="activity-day-group">
								<button
									className="activity-day-group__header"
									onClick={() => toggleDay(dayGroup.date)}
								>
									<div className="activity-day-group__left">
										<Calendar size={14} />
										<span className="activity-day-group__date">
											{dayGroup.date}
										</span>
									</div>
									<div className="activity-day-group__right">
										<span className="activity-day-group__count">
											{dayGroup.activities.length}{' '}
											{dayGroup.activities.length === 1 ? 'event' : 'events'}
										</span>
										<ChevronDown
											size={14}
											className={`activity-day-group__chevron ${isExpanded ? 'activity-day-group__chevron--expanded' : ''}`}
										/>
									</div>
								</button>

								{isExpanded && (
									<div className="activity-day-group__items">
										{dayGroup.activities.map((activity) => {
											const config = getActivityConfig(activity.type)
											const ActivityIcon = config.icon

											return (
												<div key={activity.id} className="activity-item">
													<div
														className="activity-item__icon"
														style={{ backgroundColor: `${config.color}15`, color: config.color }}
													>
														<ActivityIcon size={14} />
													</div>

													<div className="activity-item__content">
														<p className="activity-item__message">
															{activity.message}
														</p>
														<div className="activity-item__meta">
															<span className="activity-item__user">
																{activity.user}
															</span>
															<span className="activity-item__dot">•</span>
															<span className="activity-item__time">
																<Clock size={10} />
																{formatTime(activity.timestamp)}
															</span>
															<span className="activity-item__type-badge">
																{config.label}
															</span>
														</div>
													</div>
												</div>
											)
										})}
									</div>
								)}
							</div>
						)
					})}
				</div>
			) : (
				<div className="activity-tab__empty">
					<Activity size={48} />
					<h4>No Activity Found</h4>
					<p>
						{searchQuery || activeFilter !== 'all'
							? 'No activities match your search or filter criteria.'
							: 'No activity has been recorded for this project yet.'}
					</p>
					{(searchQuery || activeFilter !== 'all') && (
						<Button
							variant="outline"
							size="small"
							onClick={() => {
								setSearchQuery('')
								setActiveFilter('all')
							}}
						>
							Clear Filters
						</Button>
					)}
				</div>
			)}
		</div>
	)
}

ActivityTab.propTypes = {
	activities: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
			type: PropTypes.string,
			message: PropTypes.string.isRequired,
			user: PropTypes.string,
			timestamp: PropTypes.string,
		})
	).isRequired,
	onRefresh: PropTypes.func,
}

export default ActivityTab
